/**
 * Masks a phone number for display, showing only the first digit of area code and last 2 digits.
 * Handles various phone number formats including E.164, US formats, and international numbers.
 *
 * @param {string} phone - Phone number in any format
 * @returns {string} Masked phone number (e.g., "+1 (8**) ***-**34")
 */
export function maskPhone(phone) {
  if (!phone) return '';

  // Remove all non-digit characters for processing
  const digits = phone.replace(/\D/g, '');

  // Handle E.164 format: +1234567890 or +11234567890
  if (phone.startsWith('+')) {
    if (digits.length === 11 && digits.startsWith('1')) {
      // US: +1XXXXXXXXXX -> +1 (X**) ***-**XX
      return `+1 (${digits[1]}**) ***-**${digits.slice(-2)}`;
    } else if (digits.length === 10) {
      // US: +XXXXXXXXXX -> +1 (X**) ***-**XX (assume +1 prefix)
      return `+1 (${digits[0]}**) ***-**${digits.slice(-2)}`;
    } else if (digits.length >= 10) {
      // International: +[country][number] -> +[country] (***) ***-**[last2]
      const countryCode = digits.slice(0, -10);
      const last10 = digits.slice(-10);
      return `+${countryCode} (${last10[0]}**) ***-**${last10.slice(-2)}`;
    }
  }

  // Handle formatted US numbers: (812) 555-1234
  const match = phone.match(
    /^\+?(\d{1,3})\s*\(?(\d)\d{2}\)?\s*\d{3}-?\d{2}(\d{2})$/
  );
  if (match) {
    const [, country, areaFirst, last2] = match;
    return `+${country} (${areaFirst}**) ***-**${last2}`;
  }

  // Fallback for US numbers without + prefix
  if (digits.length === 11 && digits.startsWith('1')) {
    // US: 1XXXXXXXXXX
    return `+1 (${digits[1]}**) ***-**${digits.slice(-2)}`;
  }
  if (digits.length === 10) {
    // US: XXXXXXXXXX
    return `+1 (${digits[0]}**) ***-**${digits.slice(-2)}`;
  }

  // If we can't parse it, show last 2 digits
  if (digits.length >= 2) {
    return `***-***-${digits.slice(-2)}`;
  }

  return phone;
}

/**
 * Formats a phone number to E.164 international format (+[country code][number]).
 * Handles US numbers (10 or 11 digits) and numbers already in E.164 format.
 *
 * @param {string} phoneNumber - Phone number in any format
 * @returns {string} Phone number in E.164 format (e.g., "+1234567890")
 */
export function formatToE164(phoneNumber) {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // If it already starts with +, validate and return as is (assuming it's already E.164)
  if (phoneNumber.startsWith('+')) {
    // Validate it matches E.164 format
    if (/^\+[1-9]\d{1,14}$/.test(phoneNumber)) {
      return phoneNumber;
    }
    // If it has + but doesn't match, try to fix it
    if (digits.length >= 10) {
      return `+${digits}`;
    }
  }

  // Handle US numbers: 11 digits starting with 1, or 10 digits
  if (digits.length === 11 && digits.startsWith('1')) {
    // US: 1XXXXXXXXXX -> +1XXXXXXXXXX
    return `+${digits}`;
  }
  if (digits.length === 10) {
    // US: XXXXXXXXXX -> +1XXXXXXXXXX
    return `+1${digits}`;
  }

  // For other lengths, try to add + prefix
  if (digits.length > 10) {
    return `+${digits}`;
  }

  // If we can't format it properly, return as is
  return phoneNumber;
}

/**
 * Validates if a phone number is in E.164 format.
 * E.164 format: + followed by 1-15 digits, first digit after + must be 1-9.
 *
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if phone number is valid E.164 format
 */
export function isValidE164(phoneNumber) {
  if (!phoneNumber) return false;
  return /^\+[1-9]\d{1,14}$/.test(phoneNumber);
}

/**
 * Extracts all digits from a phone number string.
 * Removes all non-digit characters (spaces, dashes, parentheses, plus signs, etc.).
 *
 * @param {string} phoneNumber - Phone number in any format
 * @returns {string} String containing only digits
 */
export function extractDigitsFromPhone(phoneNumber) {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\D/g, '');
}

export function maskEmail(email) {
  // ms***@du************.**m
  const [user, domain] = email.split('@');
  if (!user || !domain) return email;
  const maskedUser =
    user.length > 2 ? user.slice(0, 2) + '*'.repeat(user.length - 2) : user;
  const [domainName, ...tldParts] = domain.split('.');
  const tld = tldParts.join('.');
  const maskedDomain =
    domainName.length > 2
      ? domainName.slice(0, 2) + '*'.repeat(domainName.length - 2)
      : domainName;
  let maskedTld = tld;
  if (tld.length > 1) {
    maskedTld = '*' + tld.slice(1, -1) + tld.slice(-1);
  }
  return `${maskedUser}@${maskedDomain}.${maskedTld}`;
}

/**
 * Checks if an error indicates expiration (session, code, etc.).
 * Accepts either an error object ({ code, message }) or a message string.
 *
 * @param {Object|string} err - Error object or message string
 * @returns {boolean} True if error indicates expiration
 */
export function isExpirationError(err) {
  // Handle string input (message only)
  if (typeof err === 'string') {
    const lowerMessage = err.toLowerCase();
    return (
      lowerMessage.includes('expired') ||
      lowerMessage.includes('verification code has expired') ||
      lowerMessage.includes('session has expired') ||
      (lowerMessage.includes('session') && lowerMessage.includes('expire'))
    );
  }

  // Handle error object
  const code = err?.code || err?.name;
  const message = err?.message || '';

  // Check for explicit expiration error code
  if (code === 'ExpiredCodeException') {
    return true;
  }

  // NotAuthorizedException is used for multiple scenarios, not just expiration
  // Only treat it as expiration if the message indicates expiration
  if (code === 'NotAuthorizedException' && message) {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('expired') ||
      lowerMessage.includes('verification code has expired') ||
      lowerMessage.includes('session has expired') ||
      (lowerMessage.includes('session') && lowerMessage.includes('expire'))
    );
  }

  // Check for expiration-related message strings
  if (message) {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('expired') ||
      lowerMessage.includes('verification code has expired') ||
      lowerMessage.includes('session has expired') ||
      (lowerMessage.includes('session') && lowerMessage.includes('expire'))
    );
  }

  return false;
}

// Error codes we control in our custom auth flow
const ERROR_MESSAGES = {
  CodeMismatchException: 'The code you entered is incorrect.',
  LimitExceededException: 'Too many attempts. Please try again in 15 minutes.',
};

// Cognito native errors we need to handle
const COGNITO_ERROR_MESSAGES = {
  ExpiredCodeException: 'This code has expired. Please request a new one.',
  UserNotFoundException: 'User not found. Please check your login details.',
  InvalidParameterException:
    'Unable to (re)send code. Please try again or contact support.',
  TooManyRequestsException: 'Too many requests. Please wait and try again.',
  InternalErrorException:
    'A server error occurred. Please try again later or contact support.',
};

export const CUSTOM_ERROR_MESSAGES = {
  'DefineAuthChallenge failed with error User locked out.':
    'User is locked out. Please wait 15 minutes before trying again.',
};

// Extract error from our controlled challengeResponse
export const getChallengeResponseError = (challengeResponse) => {
  if (!challengeResponse) return null;

  const challengeParams = challengeResponse.challengeParam || {};

  // We set reason='CODE_MISMATCH' in createAuth when code is wrong
  if (challengeParams.reason === 'CODE_MISMATCH') {
    return {
      code: 'CodeMismatchException',
      message: ERROR_MESSAGES.CodeMismatchException,
    };
  } else if (challengeParams.reason === 'USER_LOCKED_OUT') {
    return {
      code: 'LimitExceededException',
      message: ERROR_MESSAGES.LimitExceededException,
    };
  }

  // Challenge still pending means incorrect code (we control this)
  if (challengeResponse?.challengeName === 'CUSTOM_CHALLENGE') {
    return ERROR_MESSAGES.CodeMismatchException;
  }

  return null;
};

// Convert error to user-friendly message
export const getFriendlyError = (err) => {
  if (typeof err === 'string') {
    return err;
  }

  const code = err?.code || err?.name;
  const message = err?.message || '';

  // Handle Cognito's NotAuthorizedException (session expiration)
  if (
    code === 'NotAuthorizedException' ||
    message.includes('NotAuthorizedException')
  ) {
    if (isExpirationError(err)) {
      return 'Your verification code has expired. Please sign in again to get a new code.';
    }
    return 'Your session has expired. Please log in again.';
  }

  // Check our controlled error codes first
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }

  // Check Cognito native errors
  if (code && COGNITO_ERROR_MESSAGES[code]) {
    return COGNITO_ERROR_MESSAGES[code];
  }

  // Check custom error messages
  if (message && CUSTOM_ERROR_MESSAGES[message]) {
    return CUSTOM_ERROR_MESSAGES[message];
  }

  return 'An unknown error occurred. Please try again or contact support.';
};

/**
 * Checks if a phone number is verified in Cognito user attributes.
 * Handles both string ('true') and boolean (true) formats for phone_number_verified.
 *
 * @param {Object} user - Cognito user object with attributes
 * @returns {boolean} True if phone is verified
 */
export function isPhoneVerified(user) {
  if (!user?.attributes) return false;
  const phoneVerified = user.attributes.phone_number_verified;
  return (
    phoneVerified === 'true' ||
    phoneVerified === 'True' ||
    phoneVerified === true
  );
}

/**
 * Checks and displays warning messages from MFA challenge response.
 * Shows remaining attempts warning if user is approaching lockout threshold.
 *
 * @param {Object} challengeResponse - Cognito challenge response object
 * @param {Function} updateSnack - Function to update snackbar messages
 */
export function checkAndDisplayWarning(challengeResponse, updateSnack) {
  const challengeParams = challengeResponse.challengeParam || {};
  if (
    challengeParams.showWarning &&
    challengeParams.remainingAttempts !== undefined
  ) {
    updateSnack({
      severity: 'warning',
      message: `You have ${challengeParams.remainingAttempts} attempt${
        challengeParams.remainingAttempts !== 1 ? 's' : ''
      } remaining before lockout.`,
    });
  }
}

/**
 * Extracts client metadata for MFA challenge from API response.
 * Includes IP, location, timestamp, and selected MFA method for backend routing.
 *
 * @param {Object} data - API response data containing client metadata
 * @param {string} selectedMethod - MFA method to use ('email' or 'sms')
 * @returns {Object} Client metadata object with mfaMethod, ip, location, dateRequested, timeRequested
 */
export function extractClientMetadata(data, selectedMethod) {
  const { ip, location, dateRequested, timeRequested } = data;
  return {
    mfaMethod: selectedMethod, // 'email' or 'sms' - tells backend which method to use
    ip, // User's IP address for security logging
    dateRequested, // Date when MFA was requested
    timeRequested, // Time when MFA was requested
    location, // Geographic location (if available)
  };
}

/**
 * Extracts email address from Cognito user attributes.
 *
 * @param {Array} userAttributes - Array of Cognito user attributes from Auth.userAttributes()
 * @returns {string|undefined} User email address, or undefined if not found
 */
export function getEmailFromCognitoUser(userAttributes) {
  return userAttributes?.find((attr) => attr.Name === 'email')?.Value;
}
